import * as React from "react";
import { FunctionComponent, useState, useEffect } from "react";
import 'react-phone-number-input/style.css';
import PhoneInput from 'react-phone-number-input/input';
import { isValidPhoneNumber, formatPhoneNumber, isPossiblePhoneNumber } from "react-phone-number-input";
import { AutoClaimReport, ClaimType } from "../../../Models/Claim";

interface StepTwoProps {
	nextStep: (step: number, claim: AutoClaimReport,validate:boolean) => void;
	claim: AutoClaimReport;
	showError: boolean;
}

export const StepTwo: FunctionComponent<StepTwoProps> = ({ nextStep, claim, showError }) => {
	const [shouldScroll, setShouldScroll] = useState<boolean>(true);
	const [damageDescription, setDamageDescription] = useState<string>(claim.explanation);
	const [policyVehicleYear, setPolicyVehicleYear] = useState<string>(claim.policyHolderVehicleYear);
	const [policyVehicleMake, setPolicyVehicleMake] = useState<string>(claim.policyHolderVehicleMake);
	const [policyVehicleModel, setPolicyVehicleModel] = useState<string>(claim.policyHolderVehicleModel);
	const [policyVehicleVIN, setPolicyVehicleVIN] = useState<string>(claim.policyHolderVehicleVIN);
	const [policyVehicleDriver, setPolicyVehicleDriver] = useState<string>(claim.policyHolderDriver);
	const [policyVehicleDriverName, setPolicyVehicleDriverName] = useState<string>(claim.driverName);
	const [policyVehicleDamaged, setPolicyVehicleDamaged] = useState<string>(getLocalStorage("autoclaim-isDriveable"));
	const [policyVehicleDamagePointOfImpact, setPolicyVehicleDamagePointOfImpact] = useState<string>(claim.pointOfImpact);
	const [policyVehicleDamageAirbags, setPolicyVehicleDamageAirbags] = useState<string>(getLocalStorage("autoclaim-airbags"));
	const [policyVehicleLocation, setPolicyVehicleLocation] = useState<string>(claim.vehicleLocation);

	useEffect(() => {
		if (shouldScroll) {
			window.scrollTo({
				top: 0,
				behavior: "smooth"
			});
			setShouldScroll(false);
		}
	}, [shouldScroll])

	function getLocalStorage(key: string) {
		return localStorage.getItem(key) || ""
	}

	function isRequired() {
		if (claim.additionalAnswers["altContactRelation"]) {
			return claim.additionalAnswers["altContactRelation"] === "Policyholder";
		}
		return true;
	}

	function getRequiredAst() {
		if (isRequired())
			return <span className="red-ast">*</span>;
		return null;
	}

	return <div className="step-two">
		<div className="form-group damage-description">
			<label className="control-label col-lg-12 col-md-12 col-sm-12 col-xs-12">Please give us a description of the accident<span className="red-ast">*</span>:</label>
			<div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
				<textarea id="damage-explanation" name="Accident Explanation" aria-invalid={showError && damageDescription === ""} aria-required aria-describedby="accidentDescription" rows={5} value={damageDescription} className="form-control" maxLength={425} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
					setDamageDescription(e.target.value);
				}} />
				<p id="accidentDescription"><strong>Maximum 425 characters.  You have used {damageDescription.length} characters.</strong> <br />
					<span className={showError && damageDescription === "" ? "error" : "error-hidden"}>This field is required</span>
				</p>
			</div>
		</div>
		<div className="form-group">
			<p className="force-left col-lg-12 col-sm-12 col-xs-12 control-label">Policyholder Vehicle Involved in Accident?</p>
		</div>
		<div className="form-group">
			<div className="col-lg-2 col-md-4 col-sm-4 col-xs-4">
				<label className="control-label sr-only" htmlFor="policy-vehicle-year">Year:</label>
				<input className="form-control" type="text" inputMode="numeric" pattern="[0-9]*" maxLength={4} id="policy-vehicle-year" placeholder="2021" name="Policy Vehicle Year" value={policyVehicleYear} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
					setPolicyVehicleYear(e.target.value.replace(/\D/g, ''));
				}} />
			</div>
			<div className="col-lg-3 col-md-4 col-sm-5 col-xs-5">
				<label className="control-label sr-only" htmlFor="policy-vehicle-make">Make:</label>
				<input className="form-control" type="text" id="policy-vehicle-make" placeholder="Toyota" name="Policy Vehicle Make" value={policyVehicleMake} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
					setPolicyVehicleMake(e.target.value);
				}} />
			</div>
			<div className="col-lg-3 col-md-4 col-sm-12 col-xs-12 mobile-padding-top-xs">
				<label className="control-label sr-only" htmlFor="policy-vehicle-model">Model:</label>
				<input className="form-control" type="text" id="policy-vehicle-model" maxLength={30} placeholder="Corolla" name="Policy Vehicle Model" value={policyVehicleModel} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
					setPolicyVehicleModel(e.target.value);
				}} />
			</div>
			<div className="col-lg-4 col-md-12 col-sm-12 col-xs-12 mobile-padding-top">
				<label className="sr-only" htmlFor="policy-vehicle-vin">Vehicle License Plate:</label>
				<input className="form-control" type="text" id="policy-vehicle-vin" placeholder="Vehicle License Plate" name="Policy Vehicle License Plate" maxLength={9} value={policyVehicleVIN} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
					setPolicyVehicleVIN(e.target.value);
				}} />
			</div>
		</div>

		<div className="form-group driving-information">
			<label htmlFor="driver" className="control-label col-lg-6 col-md-6 col-sm-12 col-xs-12">Who was driving the policyholder's vehicle{getRequiredAst()}?</label>
			<div className="col-lg-6 col-md-6 col-sm-12 col-xs-12">
				<select className="form-control" id="driver" aria-required={isRequired()} aria-invalid={showError && isRequired() && policyVehicleDriver === ""} aria-describedby="policyDriverError" name="Who was driving the policy holder's vehicle?" value={policyVehicleDriver} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
					setPolicyVehicleDriver(e.target.value);
				}}>
					<option value="">Select...</option>
					<option value="Policyholder">Policyholder</option>
					<option value="Household Resident">Household Resident</option>
					<option value="Friend">Friend</option>
					<option value="Co-Worker">Co-Worker</option>
					<option value="Unoccupied">Unoccupied</option>
					<option value="Other">Other</option>
				</select>
				<p id="policyDriverError" className={showError && isRequired() && policyVehicleDriver === "" ? "error" : "error-hidden"}>This field is required</p>
			</div>
		</div>
		<div className="form-group name-of-driver">
			<label className="control-label col-lg-6 col-md-6 col-sm-12 col-xs-12" htmlFor="name-of-driver">Name of Driver:</label>
			<div className="col-lg-6 col-md-6 col-sm-12 col-xs-12">
				<input className="form-control" type="text" maxLength={45} id="name-of-driver" name="Name of Driver" placeholder="John Smith" value={policyVehicleDriverName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
					setPolicyVehicleDriverName(e.target.value);
				}} />

			</div>
		</div>
		<h2 className="damage-header">Describe damage to insured vehicle</h2>
		<div className="damage-description">
			<div className="form-group">
				<label htmlFor="driveable-vehicle" className="control-label col-lg-6 col-md-6 col-sm-12 col-xs-12">1. Is the Vehicle Driveable{getRequiredAst()}?</label>
				<div className="col-lg-6 col-md-6 col-sm-12 col-xs-12">
					<select className="form-control" id="driveable-vehicle" aria-required={isRequired()} aria-invalid={showError && isRequired() && policyVehicleDamaged === ""} aria-describedby="isDriveableError" name="Is the Vehicle Driveable?" value={policyVehicleDamaged} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
						setPolicyVehicleDamaged(e.target.value);
						localStorage.setItem("autoclaim-isDriveable", e.target.value);
					}}>
						<option value="">Select...</option>
						<option value="Yes">Yes</option>
						<option value="No">No</option>
					</select>
					<p id="isDriveableError" className={showError && isRequired() && policyVehicleDamaged === "" ? "error" : "error-hidden"}>This field is required</p>
				</div>
			</div>
			<div className="form-group">
				<label className="control-label col-lg-6 col-md-6 col-sm-12 col-xs-12" htmlFor="point-of-impact" >2. Point of Impact{getRequiredAst()}</label>
				<div className="col-lg-6 col-md-6 col-sm-12 col-xs-12">
					<select className="form-control" id="point-of-impact" aria-required={isRequired()} aria-invalid={showError && isRequired() && policyVehicleDamagePointOfImpact === ""} aria-describedby="impactError" name="What is the point of impact" value={policyVehicleDamagePointOfImpact} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
						setPolicyVehicleDamagePointOfImpact(e.target.value);
					}}>
						<option value="">Select...</option>
						<option value="Front">Front</option>
						<option value="Back">Back</option>
						<option value="Driver's Side">Driver's Side</option>
						<option value="Passenger Side">Passenger Side</option>
						<option value="Multiple">Multiple</option>
						<option value="None">None</option>
						<option value="Other">Other</option>
					</select>
					<p id="impactError" className={showError && isRequired() && policyVehicleDamagePointOfImpact === "" ? "error" : "error-hidden"}>This field is required</p>
				</div>
			</div>
			<div className="form-group">
				<label htmlFor="airbags" className="control-label col-lg-6 col-md-6 col-sm-12 col-xs-12">3. Did airbags deploy{getRequiredAst()}?</label>
				<div className="col-lg-6 col-md-6 col-sm-12 col-xs-12">
					<select className="form-control" id="airbags" aria-required={isRequired()} aria-invalid={showError && isRequired() && policyVehicleDamageAirbags === ""} aria-describedby="airbagsError" name="Did airbags deploy?" value={policyVehicleDamageAirbags} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
						setPolicyVehicleDamageAirbags(e.target.value);
						localStorage.setItem("autoclaim-airbags", e.target.value);
					}}>
						<option value="">Select...</option>
						<option value="Yes">Yes</option>
						<option value="No">No</option>
					</select>
					<p id="airbagsError" className={showError && isRequired() && policyVehicleDamageAirbags === "" ? "error" : "error-hidden"}>This field is required</p>
				</div>
			</div>

		</div>
		<div className="form-group vehicle-location">
			<label className="control-label col-lg-6 col-md-12 col-sm-12 col-xs-12" htmlFor="vehicle-location">Where can vehicle be seen (include address)?</label>
			<div className="col-lg-6 col-md-12 col-sm-6 col-xs-12">
				<input className="form-control" type="text" id="vehicle-location" maxLength={35} placeholder="acme repair, town, state" name="location of vehicle where it can be seen" value={policyVehicleLocation} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
					setPolicyVehicleLocation(e.target.value.toLocaleLowerCase());
				}} />
			</div>
		</div>
		<div className="form-group button-group">
			<button className="btn btn-primary col-lg-6 col-sm-6 col-xs-6" type="button" onClick={() => { goToStep(2); }}><i className="fa fa-arrow-left" aria-hidden="true" />&nbsp;BACK</button>
			<button className="btn btn-primary col-lg-6 col-sm-6 col-xs-6" type="button" onClick={() => { goToStep(4); }}><i className="fa fa-arrow-right" aria-hidden="true" />&nbsp;NEXT</button>
            {showError ? <button className="btn btn-warning" type="button" onClick={() => { goToStep(5,true); }}><i className="fa fa-fast-forward" aria-hidden="true" />&nbsp;Step 5</button> : null}
		</div>
	</div>;

	function goToStep(step: number, validate:boolean = false) {
		claim.explanation = damageDescription;
		claim.policyHolderVehicleYear = policyVehicleYear;
		claim.policyHolderVehicleModel = policyVehicleModel;
		claim.policyHolderVehicleMake = policyVehicleMake;
		claim.policyHolderVehicleVIN = policyVehicleVIN;
		claim.policyHolderDriver = policyVehicleDriver;
		claim.driverName = policyVehicleDriverName;
		claim.isVehicleDrivable = policyVehicleDamaged === "Yes" ? true : false;
		claim.pointOfImpact = policyVehicleDamagePointOfImpact;
		claim.didAirbagsDeploy = policyVehicleDamageAirbags === "Yes" ? true : false;
		claim.vehicleLocation = policyVehicleLocation;
		setShouldScroll(true);
		nextStep(step, claim, validate);
	}
}
